/**
 * Behavioral Balance Curve Builder
 * 
 * Produces clean, period-end-only balance data for charts.
 * Does NOT plot intra-period events (daily accrual, anniversary capitalization).
 * Only plots the balance AFTER each scheduled payment period.
 * 
 * Rules:
 * - Policy loan: one data point per payment event (post-payment balance)
 * - Standard/Intended loan: one data point per amortization row
 * - Lump sums: show as a vertical drop, then continue smooth decline
 * - No min/max/last-per-month logic
 * - No mixing of event-level accounting data
 */

import type {
  PolicyLoanLedgerRow,
  StandardLoanAmortizationRow,
} from "@/lib/calculationEngine";

export interface BehavioralCurvePoint {
  period: number;       // 1-based period index
  date: string;         // ISO date
  policyBalance?: number;
  standardBalance?: number;
  /**
   * Estimated growth of the Additional Intended Financing Margin™ after the
   * policy loan is fully repaid. = standardBalanceAtPayoff - standardBalance.
   * undefined while the policy loan is still active.
   */
  marginRecaptureBalance?: number;
}

/**
 * Build behavioral curve from policy ledger + standard amortization.
 * Extracts ONLY payment-event balances from the policy ledger,
 * paired with standard loan period-end balances.
 */
export function buildBehavioralCurve(
  policyLedger: PolicyLoanLedgerRow[],
  standardAmortization: StandardLoanAmortizationRow[],
): BehavioralCurvePoint[] {
  // Extract only payment events from policy ledger — these represent period-end balances
  const policyPayments = policyLedger.filter(e => e.eventType === 'payment');

  const maxLen = Math.max(policyPayments.length, standardAmortization.length);
  const points: BehavioralCurvePoint[] = [];

  let payoffIndex = -1;
  for (let i = 0; i < policyPayments.length; i++) {
    if (policyPayments[i].totalIndebtednessAfterEvent < 0.5) { payoffIndex = i; break; }
  }
  const stdBalAtPayoff =
    payoffIndex >= 0 && payoffIndex < standardAmortization.length
      ? standardAmortization[payoffIndex].remainingBalance
      : 0;

  for (let i = 0; i < maxLen; i++) {
    const policyRow = i < policyPayments.length ? policyPayments[i] : null;
    const stdRow = i < standardAmortization.length ? standardAmortization[i] : null;

    let policyBal = policyRow
      ? policyRow.totalIndebtednessAfterEvent
      : (policyPayments.length > 0 ? policyPayments[policyPayments.length - 1].totalIndebtednessAfterEvent : 0);

    const stdBal = stdRow ? stdRow.remainingBalance : 0;

    // Snap near-zero to zero
    if (policyBal < 0.5) policyBal = 0;

    let marginRecaptureBalance: number | undefined;
    if (payoffIndex >= 0 && i >= payoffIndex) {
      marginRecaptureBalance = Math.max(0, Math.round((stdBalAtPayoff - stdBal) * 100) / 100);
    }

    points.push({
      period: i + 1,
      date: policyRow?.date ?? stdRow?.paymentDate ?? '',
      policyBalance: Math.round(policyBal * 100) / 100,
      standardBalance: Math.round(stdBal * 100) / 100,
      marginRecaptureBalance,
    });
  }

  return points;
}

/**
 * Build behavioral curve for lump sum comparison chart.
 * Three series: original policy, policy with lump sums, intended loan.
 * All use payment-event-only extraction.
 */
export interface LumpSumCurvePoint {
  period: number;
  date: string;
  original?: number;
  withLumpSum?: number;
  standard?: number;
  /** Margin recapture vs intended-loan balance, growing AFTER lump-sum policy is paid off. */
  marginRecaptureBalance?: number;
}

export function buildLumpSumBehavioralCurve(
  originalPolicyLedger: PolicyLoanLedgerRow[],
  lumpSumPolicyLedger: PolicyLoanLedgerRow[],
  standardAmortization: StandardLoanAmortizationRow[],
): LumpSumCurvePoint[] {
  const originalPayments = originalPolicyLedger.filter(e => e.eventType === 'payment');
  const lumpSumPayments = lumpSumPolicyLedger.filter(e => e.eventType === 'payment');

  const maxLen = Math.max(originalPayments.length, lumpSumPayments.length, standardAmortization.length);
  const points: LumpSumCurvePoint[] = [];

  let lsPayoffIndex = -1;
  for (let i = 0; i < lumpSumPayments.length; i++) {
    if (lumpSumPayments[i].totalIndebtednessAfterEvent < 0.5) { lsPayoffIndex = i; break; }
  }
  const stdBalAtLsPayoff =
    lsPayoffIndex >= 0 && lsPayoffIndex < standardAmortization.length
      ? standardAmortization[lsPayoffIndex].remainingBalance
      : 0;

  for (let i = 0; i < maxLen; i++) {
    const origRow = i < originalPayments.length ? originalPayments[i] : null;
    const lsRow = i < lumpSumPayments.length ? lumpSumPayments[i] : null;
    const stdRow = i < standardAmortization.length ? standardAmortization[i] : null;

    let origBal = origRow
      ? origRow.totalIndebtednessAfterEvent
      : (originalPayments.length > 0 ? originalPayments[originalPayments.length - 1].totalIndebtednessAfterEvent : undefined);

    let lsBal = lsRow
      ? lsRow.totalIndebtednessAfterEvent
      : (lumpSumPayments.length > 0 ? lumpSumPayments[lumpSumPayments.length - 1].totalIndebtednessAfterEvent : undefined);

    const stdBal = stdRow ? stdRow.remainingBalance : undefined;

    // Snap near-zero to zero
    if (origBal !== undefined && origBal < 0.5) origBal = 0;
    if (lsBal !== undefined && lsBal < 0.5) lsBal = 0;

    // Don't extend paid-off series with trailing zeros beyond their natural end
    const origDone = origBal !== undefined && origBal === 0 && i > 0;
    const lsDone = lsBal !== undefined && lsBal === 0 && i > 0;

    const date = origRow?.date ?? lsRow?.date ?? stdRow?.paymentDate ?? '';

    points.push({
      period: i + 1,
      date,
      original: origBal !== undefined ? Math.round(origBal * 100) / 100 : undefined,
      withLumpSum: lsBal !== undefined ? Math.round(lsBal * 100) / 100 : undefined,
      standard: stdBal !== undefined ? Math.round(stdBal * 100) / 100 : undefined,
      marginRecaptureBalance:
        lsPayoffIndex >= 0 && i >= lsPayoffIndex && stdBal !== undefined
          ? Math.max(0, Math.round((stdBalAtLsPayoff - stdBal) * 100) / 100)
          : undefined,
    });

    // Stop extending a series after it hits zero (but include the zero point)
    if (origDone && lsDone && (stdBal === undefined || stdBal < 0.5)) break;
  }

  return points;
}

/**
 * Build behavioral curve for PDF/print SVG chart.
 * Returns payment-event-only points with totalIndebtedness.
 */
export interface PrintCurvePoint {
  date: string;
  balance: number;
}

export function buildPrintBehavioralCurve(
  policyLedger: PolicyLoanLedgerRow[],
): PrintCurvePoint[] {
  return policyLedger
    .filter(e => e.eventType === 'payment')
    .map(e => ({
      date: e.date,
      balance: Math.max(0, Math.round(e.totalIndebtednessAfterEvent * 100) / 100),
    }));
}
